import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function ManagementCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      designation: "",
      photo: null,
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      designation: Yup.string().required("Designation is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      photo: id ? Yup.string().nullable() : Yup.string().required("Photo is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("designation", values.designation);
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.photo) {
        formData.append("photo", values.photo);
      }

      try {
        let response;
        if (id) {
          formData.append("_method", "PUT");
          response = await request.post("management/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("management/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(id ? "Member updated successfully!" : "Member created successfully!");
          navigate("/admin/management");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchMember = async () => {
        setLoading(true);
        try {
          const response = await request.get(`management/${id}`);
          const member = response.data.data;
          formik.setValues({
            name: member.name || "",
            designation: member.designation || "",
            photo: null,
            sort_order: member.sort_order?.toString() || "",
            isActive: member.status ? "Active" : "Inactive",
          });
          setEditPhotoPreview(imagepathReturn(member.photo || null));
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchMember();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={id && id !== "new" ? "Edit Member" : "Create New Member"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/management"),
              }}
              primaryButton={{
                label: id && id !== "new" ? "Update Member" : "Create Member",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="name"
                    label="Name"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="designation"
                    label="Designation"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <FileUpload
                    name="photo"
                    formik={formik}
                    label="Upload Photo"
                    editImagePreview={editPhotoPreview}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}